import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PLAN_NAME } from "../partner-templates/components/subscription-catalog";
import { renderPartnerWriteError } from "../partner-templates/components/render-write-error";
import type {
	CreateManagedAccountInput,
	PartnerWriteError,
	PlanTier,
} from "../partner-templates/types";

export interface CreateAccountFieldErrors {
	companyName?: string;
	slug?: string;
	clientOwnerEmail?: string;
	clientOwnerName?: string;
}

export interface CreateAccountTrialDefaults {
	plan: PlanTier;
	lengthDays: number;
}

export interface PartnerCreateAccountTemplateProps {
	open: boolean;
	values: CreateManagedAccountInput;
	fieldErrors?: CreateAccountFieldErrors;
	submitting?: boolean;
	submitSuccess?: boolean;
	submitError?: PartnerWriteError | null;
	suspended?: boolean;
	trialDefaults: CreateAccountTrialDefaults;
	onChange: (next: CreateManagedAccountInput) => void;
	onSubmit?: (input: CreateManagedAccountInput) => void;
	onCancel?: () => void;
	onSetPaymentMethod?: () => void;
}

const slugify = (value: string): string =>
	value
		.toLowerCase()
		.trim()
		.replaceAll(/[^a-z0-9]+/g, "-")
		.replaceAll(/^-+|-+$/g, "");

export function PartnerCreateAccountTemplate({
	open,
	values,
	fieldErrors = {},
	submitting = false,
	submitSuccess = false,
	submitError = null,
	suspended = false,
	trialDefaults,
	onChange,
	onSubmit,
	onCancel,
	onSetPaymentMethod,
}: PartnerCreateAccountTemplateProps): React.JSX.Element {
	const slugTouchedRef = React.useRef(false);

	const handleField =
		<K extends keyof CreateManagedAccountInput>(key: K) =>
		(value: CreateManagedAccountInput[K]) => {
			const next = { ...values, [key]: value };
			if (key === "companyName" && !slugTouchedRef.current) {
				next.slug = slugify(value as string);
			}
			onChange(next);
		};

	const handleSlug = (value: string) => {
		slugTouchedRef.current = true;
		onChange({ ...values, slug: value });
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit?.(values);
	};

	const handleClose = (
		_event: object,
		reason: "backdropClick" | "escapeKeyDown",
	) => {
		if (submitting) return;
		if (reason === "backdropClick") return; // Avoid losing form data on stray clicks
		onCancel?.();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={
				submitSuccess
					? undefined
					: {
							component: "form",
							onSubmit: handleSubmit,
						}
			}>
			<DialogTitle>Create client account</DialogTitle>

			{submitSuccess ? (
				<>
					<DialogContent>
						<Alert severity="success">
							Client account created. Returning to portfolio…
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => onCancel?.()}>Close</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogContent dividers>
						<Stack spacing={3}>
							<Typography
								variant="body2"
								color="text.secondary">
								The new account starts on your default trial. The client owner
								gets a verification email to take ownership later.
							</Typography>

							{suspended && (
								<Alert severity="error">
									Your partner account is suspended; new client accounts cannot
									be created.
								</Alert>
							)}

							{submitError &&
								renderPartnerWriteError(submitError, { onSetPaymentMethod })}

							<TextField
								label="Company name"
								value={values.companyName}
								onChange={(e) => handleField("companyName")(e.target.value)}
								error={Boolean(fieldErrors.companyName)}
								helperText={fieldErrors.companyName}
								required
								fullWidth
								autoFocus
								disabled={submitting || suspended}
							/>
							<TextField
								label="Slug"
								value={values.slug}
								onChange={(e) => handleSlug(e.target.value)}
								error={Boolean(fieldErrors.slug)}
								helperText={
									fieldErrors.slug ??
									"Used in URLs. Lower-case, hyphen-separated."
								}
								required
								fullWidth
								disabled={submitting || suspended}
							/>
							<TextField
								label="Owner name"
								placeholder="Optional"
								value={values.clientOwnerName}
								onChange={(e) => handleField("clientOwnerName")(e.target.value)}
								error={Boolean(fieldErrors.clientOwnerName)}
								helperText={fieldErrors.clientOwnerName}
								fullWidth
								disabled={submitting || suspended}
							/>
							<TextField
								label="Owner email"
								type="email"
								value={values.clientOwnerEmail}
								onChange={(e) =>
									handleField("clientOwnerEmail")(e.target.value)
								}
								error={Boolean(fieldErrors.clientOwnerEmail)}
								helperText={fieldErrors.clientOwnerEmail}
								required
								fullWidth
								disabled={submitting || suspended}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={values.mangomapSupportAccess}
										onChange={(_, checked) =>
											handleField("mangomapSupportAccess")(checked)
										}
										disabled={submitting || suspended}
									/>
								}
								label="Allow MangoMap support access"
							/>

							<Box
								sx={{
									p: 2,
									borderRadius: 1,
									bgcolor: "var(--mui-palette-background-default)",
									border: "1px dashed var(--mui-palette-divider)",
								}}>
								<Typography
									variant="subtitle2"
									gutterBottom>
									Trial defaults
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary">
									{trialDefaults.lengthDays}-day trial on the{" "}
									<strong>{PLAN_NAME[trialDefaults.plan]}</strong> plan.
								</Typography>
							</Box>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => onCancel?.()}
							disabled={submitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={submitting || suspended}
							startIcon={
								submitting ? <CircularProgress size={14} /> : undefined
							}>
							{submitting ? "Creating" : "Create account"}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
